import {
  applyPageResult,
  lastPageOf,
  nextSequence,
  nextSort,
  pageRows,
  pagingQuery,
  topRowQuery,
} from '@/utils/pagedList'

/**
 * **서버 페이징·정렬 목록의 상태 한 곳** — 변환은 순수 모듈(`utils/pagedList`)이 하고 여기서는 화면에 앉힌다.
 *
 * 뷰가 갖는 것은 `search()` 하나다. 어떤 조건으로 부를지는 화면마다 다르지만, **페이지를 옮기고 크기를
 * 바꾸고 정렬 축을 고르는 일은 전부 같다** — 화면마다 적으면 「정렬을 바꾸면 1페이지로 돌아간다」 같은
 * 규칙이 한 곳에서만 지켜진다.
 *
 * ```js
 * mixins: [mainVueMixin, pagedListMixin],
 * data() { const data = initData(); data.searchForm = withPaging({ storeNm: '' }); return data },
 * methods: {
 *   search() {
 *     this.mainVue.$axios.search(this.url, this.searchQuery(), function (result) { this.applyPage(result) }.bind(this))
 *   },
 * }
 * ```
 *
 * 표는 `@sort-change="handleSortChange"` 와 정렬 열의 `sortable="custom"`, 페이지 UI 는
 * `<list-pagination>` 이 받는다.
 */
export default {
  data: function () {
    return {
      /** 서버가 말한 **전체 행수** — 페이지 UI 와 머리글의 건수가 같이 읽는다(`tableData.length` 아님) */
      totalElements: 0,
    }
  },
  computed: {
    /**
     * 열 `prop` → 서버 `sortProp` **별칭** — 중첩 값을 그리는 열을 가진 화면이 덮어쓴다.
     *
     * ```js
     * computed: { sortAliases: function () { return { 'cust.custNm': 'custNm' } } },
     * ```
     *
     * 기본은 「별칭 없음」이라 열의 `prop` 이 그대로 서버로 간다. 변환 자체는 순수 모듈
     * (`utils/pagedList.nextSort`)이 하고 여기서는 화면이 가진 표를 건네줄 뿐이다.
     */
    sortAliases: function () {
      return {}
    },
  },
  methods: {
    /**
     * **이 조회의 전제가 서 있는가** — 거짓이면 화면은 아무것도 바꾸지 않는다. 기본은 언제나 참이다.
     *
     * 대부분의 목록은 조건 없이도 물을 수 있지만, 어떤 화면은 전제를 요구한다(접속 이력의 계정·기간,
     * 배달 이력의 기간). 그 화면들이 종전에 `search()` 첫 줄에서 되돌아갔는데, **페이지·정렬·크기를
     * 바꾸는 일은 이미 끝난 뒤**였다. 그러면 요청은 안 나가는데 페이저는 2페이지를 가리키고 행 번호는
     * 51부터 세며 정렬 화살표만 옮겨 앉는다 — 표에 있는 것은 여전히 1페이지의 50건이다. 오류가 아니라
     * **화면이 자기 상태를 거짓으로 말하는** 종류라 사람은 서버가 이상하다고 읽는다.
     *
     * 그래서 판정을 상태 쓰기 **앞**으로 옮기고 그 자리를 여기 한 곳에 둔다. 아래 네 진입점
     * ([searchFirstPage]·[handlePageChange]·[handleSizeChange]·[handleSortChange])이 전부 이것을 먼저 묻는다.
     *
     * 구현은 왜 막혔는지 사람에게 말해도 된다(안내 토스트) — 아무 일도 일어나지 않는 클릭보다 낫다.
     *
     * ```js
     * methods: { canSearch: function () { return this.checkPeriod() } },
     * ```
     */
    canSearch: function () {
      return true
    },
    /**
     * 페이지 봉투를 화면에 반영한다 — 뷰의 `search()` 콜백 첫 줄.
     *
     * 서버는 요청한 페이지 번호를 **그대로** 돌려주고 범위를 넘어도 되돌리지 않는다. 그래서 마지막
     * 페이지의 마지막 행을 지우거나 조건이 바뀌어 행이 줄면 「빈 표 + 켜진 페이지 번호」에 갇힌다 —
     * `el-pagination` 은 쪽수가 줄어도 `current-change` 를 내지 않고, 같은 쪽을 눌러도 이벤트가 없다.
     * 그 한 경우(행 0건인데 전체 건수는 있다)만 마지막 유효 페이지로 내려 다시 묻는다.
     *
     * 되묻기가 무한히 이어지지 않는 근거는 **단조 감소**다: 다시 묻는 페이지는 `lastPageOf` 가 준
     * 값이라 언제나 지금보다 작고(`currPage > lastPage` 일 때만 들어온다), 0 밑으로는 내려가지 않는다.
     * 그 사이 행이 또 지워져 빈 표가 와도 다음 요청은 더 작은 페이지이므로 유한 번에 멈춘다.
     */
    applyPage: function (result) {
      const page = applyPageResult(result)

      this.searchForm.currPage = page.currPage
      this.tableData = page.tableData
      this.totalElements = page.totalElements

      const lastPage = lastPageOf(page.totalElements, this.searchForm.pageSize)
      if (page.tableData.length === 0 && page.totalElements > 0 && page.currPage > lastPage) {
        this.searchForm.currPage = lastPage
        this.search()
      }
    },
    /**
     * **조건이 바뀐 새 조회** — 1페이지로 돌아간다. 검색 버튼·필터 셀렉트·검색어 엔터가 부른다.
     *
     * 3페이지를 보다가 조건을 바꾸면 그 3페이지는 **다른 결과 집합의 3페이지**다. 그대로 물으면 조건은
     * 새것인데 위치는 옛것이라, 흔하게는 빈 표가 오고 사람은 「검색 결과가 없다」로 읽는다.
     * 화면마다 적으면 어떤 화면에서만 지켜지는 규칙이 되므로 여기 한 곳에 둔다.
     */
    searchFirstPage: function () {
      if (!this.canSearch()) return

      this.fromFirstPage()
    },
    /**
     * 1페이지로 내리고 묻는다 — **게이트를 이미 지난 호출자**용(정렬·크기 변경).
     *
     * [searchFirstPage] 를 다시 부르면 한 번의 조작에 [canSearch] 가 두 번 불린다. 지금 구현으로는
     * 두 답이 같아 탈이 없지만, 그것은 「정렬·크기를 쓰는 일이 전제를 건드리지 않는다」는 **우연**에
     * 기댄 성질이다. 전제를 읽는 축이 늘면 조용히 깨지므로 그 우연을 구조로 바꾼다 —
     * 한 조작에 게이트는 한 번, 「1페이지로 되돌린다」는 규칙은 여전히 이 한 곳이 소유한다.
     */
    fromFirstPage: function () {
      this.searchForm.currPage = 0
      this.search()
    },
    /**
     * 정렬 축 변경 — 서버로 옮긴다. 정렬이 바뀌면 **1페이지로 돌아간다**(3페이지의 3페이지는 없다).
     *
     * **`prop` 이 없는 머리글은 정렬 요청이 아니다.** 행 번호 칸처럼 `prop` 을 갖지 않은 열도 `sortable`
     * 이면 이 이벤트를 내는데, 그것을 「정렬 축 없음」으로 받아들이면 **사람이 걸어 둔 서버 정렬이
     * 지워지고 1페이지로 돌아간다** — 누른 것은 행 번호 칸인데 표의 순서가 바뀌는 셈이다. 그 열들의
     * `sortable` 은 따로 걷어냈지만, 판정은 여기 한 곳이 갖는다.
     *
     * 정렬 해제(세 번째 클릭)는 이 갈래가 아니다: `prop` 은 그대로 오고 `order` 만 `null` 이다.
     *
     * 같은 축이면 부르지 않는다 — 이미 그 상태면 서버에 물을 것이 없다.
     */
    handleSortChange: function (event) {
      if (!event || !event.prop) return

      const sort = nextSort(event, this.sortAliases)

      if (sort.sortProp === this.searchForm.sortProp && sort.sortOrder === this.searchForm.sortOrder) return
      if (!this.canSearch()) return

      this.searchForm.sortProp = sort.sortProp
      this.searchForm.sortOrder = sort.sortOrder
      this.fromFirstPage()
    },
    /**
     * 페이지 이동 — `el-pagination` 은 1-base, 서버는 0-base 다.
     *
     * **이미 그 페이지면 묻지 않는다.** 페이지 크기를 바꾸면 [handleSizeChange] 가 1페이지로 되돌리는데,
     * `el-pagination` 이 그 뒤 자기 쪽수를 다시 계산하면서 `current-change(1)` 을 덧붙여 낸다 — 같은
     * 조회가 두 번 나가고, 뒤 응답이 앞 응답을 덮으며 화면이 한 번 깜빡인다.
     */
    handlePageChange: function (page) {
      const next = Math.max(0, (page || 1) - 1)

      if (next === this.searchForm.currPage) return
      if (!this.canSearch()) return

      this.searchForm.currPage = next
      this.search()
    },
    /** 페이지 크기 변경 — 크기가 달라지면 지금 페이지 번호는 뜻을 잃으므로 1페이지로 돌아간다 */
    handleSizeChange: function (size) {
      if (!this.canSearch()) return

      this.searchForm.pageSize = size
      this.fromFirstPage()
    },
    /** 서버로 보낼 검색 조건 — 정렬 축이 비면 키를 빼서 **서버 기본 정렬**에 맡긴다 */
    searchQuery: function () {
      return pagingQuery(this.searchForm)
    },
    /**
     * 「다음 순번」을 묻는 조회 조건 — 화면이 목록과 다른 축을 얹어야 할 때 덮어쓴다.
     *
     * 기본은 화면의 검색 조건 그대로다: 최댓값은 **사람이 보고 있는 그 목록**의 최댓값이어야 한다.
     *
     * ```js
     * methods: { sequenceQuery: function (prop) { return Object.assign(topRowQuery(this.searchForm, prop), { storeCd: this.effectiveStoreCd }) } },
     * ```
     */
    sequenceQuery: function (prop) {
      return topRowQuery(this.searchForm, prop)
    },
    /**
     * **이 조건에서 다음 순번** — 내림차순 첫 행 하나만 묻는다([topRowQuery]·[nextSequence]).
     *
     * 「새 항목의 순번은 지금 있는 것 다음」을 화면이 가진 목록에서 구하면 **보고 있는 한 장**의
     * 최댓값이라, 2페이지에 더 큰 값이 있어도 모른 채 이미 쓰는 순번을 다시 내준다. 전량을 받아
     * 세는 것은 한 값을 위해 수천 행을 옮기는 일이므로, 같은 목록 API 에 한 행만 묻는다.
     *
     * **`done` 은 정확히 한 번 불린다.** 종전에는 네 화면이 각자 `.catch(() => done(1))` 을 달았는데,
     * `mainVue.$axios.search` 는 성공 콜백을 자기 `try` 안에서 부르고 **예외를 다시 던진다** — 즉
     * 창을 여는 쪽에서 오류가 나면 그 `.catch` 가 이어받아 `done(1)` 을 **두 번째로** 부른다. 다이얼로그가
     * 두 번 열리거나 사람이 고쳐 둔 순번이 1 로 되돌아간다. 걸쇠를 호출 **앞**에 걸어 구조로 막는다.
     *
     * 조회가 실패해도 `done` 을 부른다 — 창 여는 일을 성공 갈래에만 두면 조회 한 번 실패한 것이
     * 「등록 버튼이 아무 반응 없다」가 된다. 그 경우 순번은 1 이고 사람이 폼에서 고친다. 요청 실패는 헬퍼의 값
     * (`ok:false`)으로, 그 밖의 오류(창을 여는 쪽의 예외)는 거절로 온다 — 둘 다 같은 걸쇠를 지난다.
     *
     * @param {string} prop 순번 열(`'ord'`)
     * @param {function(number): void} done 1 이상의 정수를 받는다
     */
    fetchNextSequence: function (prop, done) {
      var answered = false
      var answer = function (value) {
        if (answered) return
        answered = true
        done(value)
      }

      this.mainVue.$axios
        .search(this.url, this.sequenceQuery(prop), function (result) {
          answer(nextSequence(pageRows(result), prop))
        })
        .then(function (outcome) {
          if (!outcome.ok) answer(1)
        })
        .catch(function () {
          answer(1)
        })
    },
    /**
     * 행 번호 — **전체에서 몇 번째**다(`el-table-column type="index"` 의 `:index`).
     *
     * 페이지마다 1부터 다시 세면 2페이지의 첫 행과 1페이지의 첫 행이 둘 다 「1」이 된다. 머리글은
     * 전체 건수를 말하는데 본문은 한 장만 세는 셈이라, 「1,527건 중 51번째」를 물을 방법이 없어진다.
     */
    indexMethod: function (index) {
      return 1 + index + this.searchForm.currPage * this.searchForm.pageSize
    },
  },
}
